import { Link } from 'react-router-dom';
import SmartImage from '@/components/base/SmartImage';
import { getProductImagePath, type AyushProduct } from '@/pages/products/productUtils';

interface ProductCardProps {
  product: AyushProduct;
  categoryLabel: string;
}

function Stars({ rating }: { rating: number }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <span className="flex items-center gap-0.5">
      {Array.from({ length: full }).map((_, index) => (
        <i key={`full-${index}`} className="ri-star-fill text-accent-500 text-xs"></i>
      ))}
      {half ? <i className="ri-star-half-fill text-accent-500 text-xs"></i> : null}
      <span className="text-xs text-foreground-500 ml-1">{rating.toFixed(1)}</span>
    </span>
  );
}

export default function ProductCard({ product, categoryLabel }: ProductCardProps) {
  return (
    <article className="group h-full flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors">
      <div className="relative w-full aspect-square bg-background-100 flex items-center justify-center overflow-hidden">
        <SmartImage
          preferredSrc={getProductImagePath(product.id)}
          fallbackSrc={product.image}
          alt={`${product.name} - Ayurvedic ${categoryLabel} by Best Research Pharma`}
          title={`${product.name} - Best Research Pharma`}
          className="max-h-[78%] max-w-[78%] object-contain transition-transform duration-700 group-hover:scale-105"
        />
        {product.bestSeller ? (
          <span className="absolute top-3 left-3 px-2 py-0.5 rounded bg-accent-400 text-primary-950 text-[10px] font-bold uppercase tracking-wide">
            Best Seller
          </span>
        ) : null}
        <span className="absolute top-3 right-3 px-2 py-0.5 rounded bg-secondary-100 text-secondary-900 text-[10px] font-semibold">
          {categoryLabel}
        </span>
        {product.trademarked ? (
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-primary-950/85 text-background-50 text-[10px] font-semibold tracking-wide">
            <i className="ri-award-fill text-accent-400"></i>
            ® Registered Trade Mark
          </span>
        ) : null}
      </div>

      <div className="flex flex-col flex-1 p-4">
        <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 leading-snug">
          {product.name}
        </h3>

        <div className="mt-1.5 flex items-center justify-between gap-2">
          <Stars rating={product.rating} />
        </div>

        <p className="mt-2 text-xs text-foreground-600 line-clamp-2 leading-relaxed">{product.description}</p>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary-50 text-secondary-800">
            <i className="ri-hand-heart-line"></i>
            {product.concern}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-background-100 text-foreground-600">
            <i className="ri-archive-2-line"></i>
            {product.pack}
          </span>
        </div>

        <div className="mt-auto pt-4">
          <p className="mb-3 text-xs text-foreground-500 flex items-center gap-1.5">
            <i className="ri-price-tag-3-line text-primary-600"></i>
            Price on enquiry
          </p>
          <Link
            to="/contact"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-xs md:text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-send-plane-fill text-sm"></i>
            Enquire Now
          </Link>
        </div>
      </div>
    </article>
  );
}